<template>
    <article v-if="hasItem" class="delivery-item">
        <header class="delivery-item__header">
            <h3 class="delivery-item__name fw-bolder mb-0">{{itemName}}</h3>
        </header>

        <div class="delivery-item__controls">
            <div class="delivery-item__metric">
                <span class="delivery-item__label">{{$t('delivery.requested')}}</span>
                <strong class="delivery-item__requested">{{requestedQuantity}} <small>{{unit}}</small></strong>
            </div>

            <div class="delivery-item__metric delivery-item__metric--quantity">
                <label :for="inputId" class="delivery-item__label">{{$t('delivery.prepared')}}</label>
                <div class="delivery-item__stepper">
                    <button
                    type="button"
                    class="delivery-item__stepper-button"
                    :disabled="!canProcess"
                    :aria-label="$t('delivery.decrease_quantity')"
                    @click.stop.prevent="decrement_quantity($event, item)"
                    >−</button>
                    <input
                    :id="inputId"
                    v-model="item._pivot_new_quantity"
                    type="number"
                    inputmode="decimal"
                    class="delivery-item__quantity-input form-control"
                    :disabled="!canProcess"
                    >
                    <button
                    type="button"
                    class="delivery-item__stepper-button"
                    :disabled="!canProcess"
                    :aria-label="$t('delivery.increase_quantity')"
                    @click.stop.prevent="increment_quantity($event, item)"
                    >+</button>
                </div>
            </div>

            <div class="delivery-item__metric delivery-item__metric--processed">
                <span class="delivery-item__label">{{$t('delivery.processed')}}</span>
                <button
                type="button"
                class="delivery-item__processed-button"
                :class="{'delivery-item__processed-button--active': isPrepared}"
                :disabled="!canProcess"
                :aria-label="$t('delivery.toggle_processed')"
                :aria-pressed="isPrepared ? 'true' : 'false'"
                @click="toggle_prepared($event)"
                >
                    <BootstrapIcon :name="isPrepared ? 'check-square-fill' : 'square'" :size="27" />
                </button>
            </div>
        </div>

        <div v-if="fieldSets.length > 0" class="delivery-item__fields">
            <div v-for="(set, index) in fieldSets" :key="index" class="delivery-item__field-set">
                <p v-for="(field, fieldIndex) in set" :key="fieldIndex" class="delivery-item__field mb-0">
                    <strong v-if="field.name">{{field.name}}:</strong>
                    <span>{{field.value}}</span>
                </p>
            </div>
        </div>
    </article>
</template>

<script>
import InventoryItemStore from '@/common/store/inventory_item.js'
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'

export default {
    name: 'DeliveryItem',
    components: {
        BootstrapIcon
    },
    props: {
        order_status: {
            type: String,
            default: ''
        },
        _item: {
            type: Object,
            default: () => ({})
        }
    },
    data: function () {
        return {
            item: {},
            timeout: null
        }
    },
    computed: {
        hasItem: function () {
            return this.item && typeof this.item === 'object' && !Array.isArray(this.item) && this.hasText(this.item.id)
        },
        itemName: function () {
            return this.hasText(this.item.name) ? this.item.name.trim() : this.$t('order_widget.unnamed_product')
        },
        requestedQuantity: function () {
            const quantity = this.item._pivot_quantity
            return typeof quantity === 'number' || this.hasText(quantity) ? quantity : this.$t('order_widget.quantity_unavailable')
        },
        unit: function () {
            if (typeof this.item.unit !== 'number' && !this.hasText(this.item.unit)) return ''
            const key = `units.unit_abbr_${this.item.unit}`
            return typeof this.$te === 'function' && this.$te(key) ? this.$t(key) : ''
        },
        canProcess: function () {
            return this.order_status === 'order_preparing'
        },
        isPrepared: function () {
            return Number(this.item._pivot_prepared) === 1
        },
        inputId: function () {
            const suffix = this.hasText(this.item.id) ? this.item.id.replace(/[^a-zA-Z0-9_-]/g, '-') : 'item'
            return `delivery-item-quantity-${suffix}`
        },
        fieldSets: function () {
            const fields = this.item._pivot_data && typeof this.item._pivot_data === 'object'
                ? this.item._pivot_data.fields
                : null
            if (!Array.isArray(fields)) return []

            return fields
                .filter(set => Array.isArray(set))
                .map(set => set
                    .filter(field => field && typeof field === 'object' && this.hasDisplayValue(field.value))
                    .map(field => ({
                        name: this.hasText(field.name) ? field.name.trim() : '',
                        value: this.displayValue(field.value)
                    })))
                .filter(set => set.length > 0)
        }
    },
    watch: {
        'item._pivot_new_quantity': function (newValue, oldValue) {
            if (typeof newValue === 'number' && typeof oldValue === 'number' && oldValue !== newValue) this.scheduleUpdate()
        },
        'item._pivot_prepared': function (newValue, oldValue) {
            if (typeof newValue === 'number' && typeof oldValue === 'number' && oldValue !== newValue) this.scheduleUpdate()
        }
    },
    created: function () {
        this.item = this._item && typeof this._item === 'object' && !Array.isArray(this._item) ? this._item : {}
    },
    beforeUnmount: function () {
        clearTimeout(this.timeout)
    },
    methods: {
        hasText: function (value) {
            return typeof value === 'string' && value.trim().length > 0
        },
        hasDisplayValue: function (value) {
            if (this.hasText(value)) return true
            if (typeof value === 'number' || typeof value === 'boolean') return true
            return Array.isArray(value) ? value.length > 0 : false
        },
        displayValue: function (value) {
            if (Array.isArray(value)) return value.map(this.displayValue).filter(Boolean).join(', ')
            return value === null || typeof value === 'undefined' ? '' : String(value)
        },
        scheduleUpdate: function () {
            clearTimeout(this.timeout)
            this.timeout = setTimeout(() => this.update_delivery_item(), 1000)
        },
        update_delivery_item: async function () {
            if (!this.hasText(this.item._pivot_id)) return
            await InventoryItemStore.dispatch('UPDATE_DELIVERY_ITEM', {
                id: this.item._pivot_id,
                params: {
                    new_quantity: this.item._pivot_new_quantity,
                    prepared: this.item._pivot_prepared
                }
            })
        },
        increment_quantity: function ($event, item) {
            $event.stopPropagation()
            const quantity = Number(item._pivot_new_quantity)
            item._pivot_new_quantity = Number.isFinite(quantity) ? quantity + 1 : 1
        },
        decrement_quantity: function ($event, item) {
            $event.stopPropagation()
            const quantity = Number(item._pivot_new_quantity)
            item._pivot_new_quantity = Number.isFinite(quantity) && quantity > 1 ? quantity - 1 : 0
        },
        toggle_prepared: function ($event) {
            $event.stopPropagation()
            if (!this.canProcess) return
            this.item._pivot_prepared = this.isPrepared ? 0 : 1
        }
    }
}
</script>
